const getTeamIds = (game) => {
  const ids = [game?.teams?.teamA?.id, game?.teams?.teamB?.id].filter(Boolean);
  return ids;
};

const getVenueId = (venue) => {
  if (!venue) {
    return '';
  }

  if (typeof venue === 'string') {
    return venue;
  }

  if (typeof venue === 'object') {
    return venue.id || '';
  }

  return '';
};

const getTeamNamesById = (game) => {
  const map = new Map();

  if (game?.teams?.teamA?.id) {
    map.set(game.teams.teamA.id, game.teams.teamA.name || 'Unknown team');
  }

  if (game?.teams?.teamB?.id) {
    map.set(game.teams.teamB.id, game.teams.teamB.name || 'Unknown team');
  }

  return map;
};

const makeSlotKey = (venueId, green, rink) => `${venueId}::${green}::${Number(rink)}`;

const parseSlotKey = (slotKey) => {
  const [venueId, green, rink] = String(slotKey || '').split('::');
  return {
    venueId: venueId || '',
    green: green || '',
    rink: Number(rink) || 0
  };
};

const normalizeDirection = (direction) => {
  const value = Number(direction || 1);
  return value === 2 ? 2 : 1;
};

const makeDirectionalSlotKey = (slotKey, direction) =>
  `${slotKey}::dir${normalizeDirection(direction)}`;

const isEndRink = (rinkNumber) => {
  const rink = Number(rinkNumber || 0);
  return rink === 1 || rink === 6;
};

const isPlayableCurrentRoundGame = (game) => Number(game?.status) !== 3;

const buildActiveSlots = (event) => {
  if (!Array.isArray(event?.greens)) {
    return [];
  }

  const slots = [];

  event.greens.forEach((venue) => {
    const orderedGreens = [...(venue.greens || [])].sort((a, b) =>
      String(a?.name || '').localeCompare(String(b?.name || ''), undefined, {
        numeric: true,
        sensitivity: 'base'
      })
    );

    orderedGreens.forEach((greenObj) => {
      const orderedRinks = [...(greenObj.rinks || [])].sort(
        (a, b) => Number(a?.rink || 0) - Number(b?.rink || 0)
      );

      orderedRinks.forEach((rinkObj) => {
        if (!rinkObj?.active) {
          return;
        }

        slots.push({
          key: makeSlotKey(venue.id, greenObj.name, rinkObj.rink),
          venueId: venue.id,
          venueName: venue.name || '',
          green: greenObj.name,
          rink: Number(rinkObj.rink)
        });
      });
    });
  });

  return slots;
};

const scoreTeamPlacement = ({ historicalCount, playedPreviousRoundHere }) => {
  const projectedCount = historicalCount + 1;

  // Primary constraint: avoid immediate repeat on same rink from previous round.
  const immediateRepeatPenalty = playedPreviousRoundHere ? 3 : 0;

  // Secondary constraint: avoid over-using a rink for the same team.
  // 1 time is fine, 2nd time should still be nudged, 3+ should be strongly penalized.
  let overusePenalty = 0;
  if (projectedCount === 2) {
    overusePenalty = 1;
  } else if (projectedCount >= 3) {
    overusePenalty = 2 + (projectedCount - 3) * 2;
  }

  return {
    projectedCount,
    immediateRepeatPenalty,
    overusePenalty,
    teamPenalty: immediateRepeatPenalty + overusePenalty
  };
};

const describeTeamReason = ({ teamName, playedPreviousRoundHere, projectedCount }) => {
  if (playedPreviousRoundHere) {
    return `${teamName} was on this rink last round.`;
  }

  if (projectedCount === 2) {
    return `${teamName} has already used this rink once.`;
  }

  if (projectedCount >= 3) {
    return `${teamName} would be on this rink for the ${ordinal(projectedCount)} time.`;
  }

  return '';
};

const ordinal = (n) => {
  if (n === 1) return '1st';
  if (n === 2) return '2nd';
  if (n === 3) return '3rd';
  return `${n}th`;
};

const getForwardDistance = (fromIndex, toIndex, total) => {
  if (fromIndex < 0 || toIndex < 0 || total <= 0) {
    return Number.MAX_SAFE_INTEGER;
  }

  if (fromIndex === toIndex) {
    return 0;
  }

  return (toIndex - fromIndex + total) % total;
};

const getProjectedPriority = (details) => {
  const safeDetails = Array.isArray(details) ? details : [];

  const projectedCounts = safeDetails.map((d) => Number(d?.projectedCount || 0));
  const maxProjected = projectedCounts.length ? Math.max(...projectedCounts) : 0;
  const minProjected = projectedCounts.length ? Math.min(...projectedCounts) : 0;
  const immediateRepeatCount = safeDetails.filter((d) => d.playedPreviousRoundHere).length;

  return {
    maxProjected,
    minProjected,
    immediateRepeatCount
  };
};

export const getGreenSuggestionRound = (event, games, selectedRound) => {
  const safeGames = Array.isArray(games) ? games : [];

  if (selectedRound !== 'all') {
    return Number(selectedRound) || 1;
  }

  const uniqueRounds = Array.from(
    new Set(safeGames.map((game) => Number(game?.round || 0)).filter((r) => r > 0))
  ).sort((a, b) => a - b);

  const maxRoundWithGames = uniqueRounds[uniqueRounds.length - 1] || 0;

  const getRoundPressure = (round) => {
    const historicalGames = safeGames.filter((game) => Number(game?.round || 0) < round);
    const previousRoundGames = safeGames.filter((game) => Number(game?.round || 0) === round - 1);
    const currentRoundGames = safeGames.filter(
      (game) => Number(game?.round || 0) === round && isPlayableCurrentRoundGame(game)
    );

    if (!currentRoundGames.length) {
      return 0;
    }

    const historicalCounts = new Map();
    const previousRoundSlotByTeam = new Map();

    historicalGames.forEach((game) => {
      const slotKey = makeSlotKey(getVenueId(game?.venue), game?.green, game?.rink);
      const directionalSlotKey = makeDirectionalSlotKey(slotKey, game?.greenDirection);
      getTeamIds(game).forEach((teamId) => {
        const key = `${teamId}::${directionalSlotKey}`;
        historicalCounts.set(key, (historicalCounts.get(key) || 0) + 1);
      });
    });

    previousRoundGames.forEach((game) => {
      const slotKey = makeSlotKey(getVenueId(game?.venue), game?.green, game?.rink);
      const directionalSlotKey = makeDirectionalSlotKey(slotKey, game?.greenDirection);
      getTeamIds(game).forEach((teamId) => {
        previousRoundSlotByTeam.set(teamId, directionalSlotKey);
      });
    });

    let score = 0;

    currentRoundGames.forEach((game) => {
      const slotKey = makeSlotKey(getVenueId(game?.venue), game?.green, game?.rink);
      const directionalSlotKey = makeDirectionalSlotKey(slotKey, game?.greenDirection);
      getTeamIds(game).forEach((teamId) => {
        const historicalCount = historicalCounts.get(`${teamId}::${directionalSlotKey}`) || 0;
        const playedPreviousRoundHere = previousRoundSlotByTeam.get(teamId) === directionalSlotKey;

        if (playedPreviousRoundHere) {
          score += 3;
        }

        if (historicalCount >= 1) {
          score += historicalCount;
        }
      });
    });

    return score;
  };

  const bestRoundByPressure = uniqueRounds.reduce(
    (best, round) => {
      const pressure = getRoundPressure(round);
      if (pressure > best.pressure) {
        return { round, pressure };
      }

      if (pressure === best.pressure && round > best.round) {
        return { round, pressure };
      }

      return best;
    },
    { round: 0, pressure: -1 }
  );

  const currentRound = Number(event?.currentRound || 0);

  // Prefer rounds that actually have repeat pressure when viewing "All Rounds".
  if (bestRoundByPressure.round > 0 && bestRoundByPressure.pressure > 0) {
    return bestRoundByPressure.round;
  }

  // Otherwise prefer event.currentRound only when it has games.
  if (
    currentRound > 0 &&
    safeGames.some(
      (game) => Number(game?.round || 0) === currentRound && isPlayableCurrentRoundGame(game)
    )
  ) {
    return currentRound;
  }

  // Fallback to the latest round that has games.
  if (maxRoundWithGames > 0) {
    return maxRoundWithGames;
  }

  return currentRound > 0 ? currentRound : 1;
};

export const buildGreenSwapSuggestions = ({
  event,
  games,
  selectedRound,
  avoidTeamsOverusedOnEndRinks = false
}) => {

  if (!event || !Array.isArray(games) || games.length === 0) {
    return { round: 0, suggestions: [], remainingIssues: [] };
  }

  const round = getGreenSuggestionRound(event, games, selectedRound);
  const activeSlots = buildActiveSlots(event);
  if (activeSlots.length === 0) {
    return { round, suggestions: [], remainingIssues: [] };
  }

  const historicalGames = games.filter((game) => Number(game?.round || 0) < round);
  const previousRoundGames = games.filter((game) => Number(game?.round || 0) === round - 1);
  const currentRoundGames = games.filter(
    (game) => Number(game?.round || 0) === round && isPlayableCurrentRoundGame(game)
  );

  if (currentRoundGames.length === 0) {
    return { round, suggestions: [], remainingIssues: [] };
  }

  const historicalCounts = new Map(); // key: teamId::slotKey -> count
  const historicalEndCountsByTeam = new Map(); // key: teamId -> end rink historical count
  const previousRoundSlotByTeam = new Map(); // key: teamId -> slotKey

  historicalGames.forEach((game) => {
    const slotKey = makeSlotKey(getVenueId(game?.venue), game?.green, game?.rink);
    const directionalSlotKey = makeDirectionalSlotKey(slotKey, game?.greenDirection);
    const onEndRink = isEndRink(game?.rink);

    getTeamIds(game).forEach((teamId) => {
      const key = `${teamId}::${directionalSlotKey}`;
      historicalCounts.set(key, (historicalCounts.get(key) || 0) + 1);

      if (onEndRink) {
        historicalEndCountsByTeam.set(teamId, (historicalEndCountsByTeam.get(teamId) || 0) + 1);
      }
    });
  });

  previousRoundGames.forEach((game) => {
    const slotKey = makeSlotKey(getVenueId(game?.venue), game?.green, game?.rink);
    const directionalSlotKey = makeDirectionalSlotKey(slotKey, game?.greenDirection);
    getTeamIds(game).forEach((teamId) => {
      previousRoundSlotByTeam.set(teamId, directionalSlotKey);
    });
  });

  const gameMetaById = new Map();
  const assignmentsByGameId = new Map();
  const occupantBySlot = new Map();
  const slotIndexByKey = new Map(activeSlots.map((slot, idx) => [slot.key, idx]));

  currentRoundGames.forEach((game) => {
    gameMetaById.set(game.id, {
      id: game.id,
      name: game.name || 'Unnamed game',
      teams: getTeamIds(game),
      teamNamesById: getTeamNamesById(game),
      direction: normalizeDirection(game?.greenDirection)
    });

    const slotKey = makeSlotKey(getVenueId(game?.venue), game?.green, game?.rink);
    assignmentsByGameId.set(game.id, slotKey);
    occupantBySlot.set(slotKey, game.id);
  });

  const evaluatePlacement = (gameId, slotKey) => {
    const meta = gameMetaById.get(gameId);
    if (!meta || !slotKey) {
      return { penalty: 0, details: [] };
    }

    const slotMeta = parseSlotKey(slotKey);
    const slotIsEndRink = isEndRink(slotMeta.rink);

    const details = meta.teams.map((teamId) => {
      const directionalSlotKey = makeDirectionalSlotKey(slotKey, meta.direction);
      const historicalCount = historicalCounts.get(`${teamId}::${directionalSlotKey}`) || 0;
      const historicalEndCount = historicalEndCountsByTeam.get(teamId) || 0;
      const playedPreviousRoundHere = previousRoundSlotByTeam.get(teamId) === directionalSlotKey;
      const score = scoreTeamPlacement({ historicalCount, playedPreviousRoundHere });
      const projectedEndCount = slotIsEndRink ? historicalEndCount + 1 : historicalEndCount;

      let endRinkOverusePenalty = 0;
      if (avoidTeamsOverusedOnEndRinks && slotIsEndRink && historicalEndCount > 2) {
        // Strongly discourage re-assigning to end rinks after 2 prior end-rink games.
        endRinkOverusePenalty = 2 + (historicalEndCount - 2);
      }

      const endRinkReason =
        avoidTeamsOverusedOnEndRinks && slotIsEndRink && historicalEndCount > 2
          ? `${meta.teamNamesById.get(teamId) || 'Unknown team'} already has ${historicalEndCount} prior end-rink games; this would become ${projectedEndCount}.`
          : '';

      const baseReason = describeTeamReason({
        teamName: meta.teamNamesById.get(teamId) || 'Unknown team',
        playedPreviousRoundHere,
        projectedCount: score.projectedCount
      });

      return {
        teamId,
        teamName: meta.teamNamesById.get(teamId) || 'Unknown team',
        historicalCount,
        historicalEndCount,
        projectedEndCount,
        slotIsEndRink,
        playedPreviousRoundHere,
        ...score,
        endRinkOverusePenalty,
        teamPenalty: score.teamPenalty + endRinkOverusePenalty,
        reason: endRinkReason || baseReason
      };
    });

    return {
      penalty: details.reduce((sum, d) => sum + d.teamPenalty, 0),
      details
    };
  };

  const gameIdsByPriority = currentRoundGames
    .map((g) => g.id)
    .sort((a, b) => {
      const aEval = evaluatePlacement(a, assignmentsByGameId.get(a));
      const bEval = evaluatePlacement(b, assignmentsByGameId.get(b));
      return bEval.penalty - aEval.penalty;
    });

  const suggestions = [];
  const touchedGames = new Set();

  gameIdsByPriority.forEach((gameId) => {
    if (touchedGames.has(gameId)) {
      return;
    }

    const currentSlot = assignmentsByGameId.get(gameId);
    const currentSlotMeta = parseSlotKey(currentSlot);
    const currentEval = evaluatePlacement(gameId, currentSlot);
    const requiresDifferentRink = currentEval.details.some(
      (d) => d.playedPreviousRoundHere || d.historicalCount >= 1
    );

    if (currentEval.penalty <= 0) {
      return;
    }

    const currentPriority = getProjectedPriority(currentEval.details);
    const isBetterCandidate = (candidate, bestCandidate, preferSameGreenTie = false) => {
      if (!bestCandidate) {
        return true;
      }

      if (requiresDifferentRink) {
        if (candidate.sourceMaxProjected !== bestCandidate.sourceMaxProjected) {
          return candidate.sourceMaxProjected < bestCandidate.sourceMaxProjected;
        }

        if (candidate.sourceImmediateRepeatCount !== bestCandidate.sourceImmediateRepeatCount) {
          return candidate.sourceImmediateRepeatCount < bestCandidate.sourceImmediateRepeatCount;
        }

        if (candidate.totalPostPenalty !== bestCandidate.totalPostPenalty) {
          return candidate.totalPostPenalty < bestCandidate.totalPostPenalty;
        }

        if (candidate.collateralPressure !== bestCandidate.collateralPressure) {
          return candidate.collateralPressure < bestCandidate.collateralPressure;
        }

        if (candidate.movedEval.penalty !== bestCandidate.movedEval.penalty) {
          return candidate.movedEval.penalty < bestCandidate.movedEval.penalty;
        }

        if (preferSameGreenTie && candidate.greenChangeTier !== bestCandidate.greenChangeTier) {
          return candidate.greenChangeTier < bestCandidate.greenChangeTier;
        }

        if (candidate.forwardDistance !== bestCandidate.forwardDistance) {
          return candidate.forwardDistance < bestCandidate.forwardDistance;
        }

        if (candidate.sourceMinProjected !== bestCandidate.sourceMinProjected) {
          return candidate.sourceMinProjected < bestCandidate.sourceMinProjected;
        }

        return candidate.improvement > bestCandidate.improvement;
      }

      if (candidate.improvement !== bestCandidate.improvement) {
        return candidate.improvement > bestCandidate.improvement;
      }

      if (candidate.greenChangeTier !== bestCandidate.greenChangeTier) {
        return candidate.greenChangeTier < bestCandidate.greenChangeTier;
      }

      return candidate.forwardDistance < bestCandidate.forwardDistance;
    };

    let bestAny = null;
    let bestSameGreenViable = null;

    activeSlots.forEach((slot) => {
      if (slot.key === currentSlot) {
        return;
      }

      const otherGameId = occupantBySlot.get(slot.key) || null;
      const movedEval = evaluatePlacement(gameId, slot.key);

      let improvement = currentEval.penalty - movedEval.penalty;
      let otherCurrentEval = null;
      let otherMovedEval = null;

      if (otherGameId) {
        const otherCurrentSlot = assignmentsByGameId.get(otherGameId);
        otherCurrentEval = evaluatePlacement(otherGameId, otherCurrentSlot);
        otherMovedEval = evaluatePlacement(otherGameId, currentSlot);
        improvement =
          currentEval.penalty + otherCurrentEval.penalty - (movedEval.penalty + otherMovedEval.penalty);
      }

      const sourcePriority = getProjectedPriority(movedEval.details);
      const otherMovedPenalty = otherMovedEval?.penalty || 0;
      const totalPostPenalty = movedEval.penalty + otherMovedPenalty;
      const collateralPressure = otherMovedPenalty > 0 ? 1 : 0;
      const fromIndex = slotIndexByKey.get(currentSlot);
      const toIndex = slotIndexByKey.get(slot.key);
      const forwardDistance = getForwardDistance(fromIndex, toIndex, activeSlots.length);
      const greenChangeTier = slot.green === currentSlotMeta.green ? 0 : 1;
      const candidate = {
        slot,
        otherGameId,
        movedEval,
        otherCurrentEval,
        otherMovedEval,
        improvement,
        sourceMaxProjected: sourcePriority.maxProjected,
        sourceMinProjected: sourcePriority.minProjected,
        sourceImmediateRepeatCount: sourcePriority.immediateRepeatCount,
        totalPostPenalty,
        collateralPressure,
        forwardDistance,
        greenChangeTier
      };

      const viableSameGreenCandidate =
        requiresDifferentRink &&
        greenChangeTier === 0 &&
        (improvement > 0 ||
          sourcePriority.maxProjected < currentPriority.maxProjected ||
          sourcePriority.immediateRepeatCount < currentPriority.immediateRepeatCount ||
          movedEval.penalty < currentEval.penalty);

      if (isBetterCandidate(candidate, bestAny, false)) {
        bestAny = candidate;
      }

      if (viableSameGreenCandidate && isBetterCandidate(candidate, bestSameGreenViable, true)) {
        bestSameGreenViable = candidate;
      }
    });

    let best = bestAny;

    if (requiresDifferentRink) {
      if (bestSameGreenViable) {
        const bestCrossGreen =
          bestAny && bestAny.greenChangeTier > 0 ? bestAny : null;

        const crossGreenClearlyBetter =
          Boolean(bestCrossGreen) &&
          (bestCrossGreen.sourceMaxProjected < bestSameGreenViable.sourceMaxProjected ||
            bestCrossGreen.sourceImmediateRepeatCount <
              bestSameGreenViable.sourceImmediateRepeatCount ||
            bestCrossGreen.movedEval.penalty < bestSameGreenViable.movedEval.penalty ||
            bestCrossGreen.totalPostPenalty < bestSameGreenViable.totalPostPenalty);

        best = crossGreenClearlyBetter ? bestCrossGreen : bestSameGreenViable;
      }
    }

    if (!best) {
      return;
    }

    if (!requiresDifferentRink && best.improvement <= 0) {
      return;
    }

    const targetSlot = best.slot;
    const targetMeta = parseSlotKey(targetSlot.key);
    const targetGameId = best.otherGameId;

    if (targetGameId) {
      assignmentsByGameId.set(gameId, targetSlot.key);
      assignmentsByGameId.set(targetGameId, currentSlot);
      occupantBySlot.set(targetSlot.key, gameId);
      occupantBySlot.set(currentSlot, targetGameId);

      touchedGames.add(gameId);

      const fromSlotMeta = parseSlotKey(currentSlot);
      const otherMeta = gameMetaById.get(targetGameId);
      const movedMainDetails = (best.movedEval?.details || []).filter((d) => d.reason);
      const movedOtherDetails = (best.otherMovedEval?.details || []).filter((d) => d.reason);

      suggestions.push({
        id: `${gameId}-${targetGameId}-${targetSlot.key}`,
        type: 'swap',
        round,
        sourceGameId: gameId,
        sourceGameName: gameMetaById.get(gameId)?.name || 'Unnamed game',
        targetGameId,
        targetGameName: otherMeta?.name || 'Unnamed game',
        from: fromSlotMeta,
        to: targetMeta,
        netImprovement: best.improvement,
        unavoidable:
          best.improvement <= 0 ||
          (best.sourceMaxProjected || 0) >= 3 ||
          best.movedEval.penalty > 0 ||
          (best.otherMovedEval?.penalty || 0) > 0,
        note:
          (best.sourceMaxProjected || 0) <= 1
            ? `Swap to the nearest suitable rink for Round ${round}.`
            : (best.sourceMaxProjected || 0) === 2
              ? `Swap to a nearby rink to reduce repeat usage in Round ${round}.`
              : `Swap helps reduce repeat pressure, but some repeats may still remain in Round ${round}.`,
        why: [
          ...movedMainDetails.map((d) =>
            d.playedPreviousRoundHere
              ? `${d.teamName}: this avoids using the same rink as last round (${targetMeta.green}${targetMeta.rink}).`
              : `${d.teamName}: ${d.reason}`
          ),
          ...movedOtherDetails.map((d) =>
            d.playedPreviousRoundHere
              ? `${d.teamName}: this team still repeats last round's rink right now.`
              : `${d.teamName}: ${d.reason}`
          )
        ].slice(0, 4)
      });
    } else {
      assignmentsByGameId.set(gameId, targetSlot.key);
      occupantBySlot.delete(currentSlot);
      occupantBySlot.set(targetSlot.key, gameId);
      touchedGames.add(gameId);

      const fromSlotMeta = parseSlotKey(currentSlot);
      const movedMainDetails = (best.movedEval?.details || []).filter((d) => d.reason);

      suggestions.push({
        id: `${gameId}-${targetSlot.key}`,
        type: 'move',
        round,
        sourceGameId: gameId,
        sourceGameName: gameMetaById.get(gameId)?.name || 'Unnamed game',
        targetGameId: null,
        targetGameName: '',
        from: fromSlotMeta,
        to: targetMeta,
        netImprovement: best.improvement,
        unavoidable:
          best.improvement <= 0 ||
          (best.sourceMaxProjected || 0) >= 3 ||
          best.movedEval.penalty > 0,
        note:
          (best.sourceMaxProjected || 0) <= 1
            ? `Move to the nearest suitable rink for Round ${round}.`
            : (best.sourceMaxProjected || 0) === 2
              ? `Move to a nearby rink to reduce repeat usage in Round ${round}.`
              : `Move helps reduce repeat pressure, but some repeats may still remain in Round ${round}.`,
        why: movedMainDetails.map((d) =>
          d.playedPreviousRoundHere
            ? `${d.teamName}: this avoids using the same rink as last round (${targetMeta.green}${targetMeta.rink}).`
            : `${d.teamName}: ${d.reason}`
        )
      });
    }
  });

  const remainingIssues = [];
  gameIdsByPriority.forEach((gameId) => {
    const slotKey = assignmentsByGameId.get(gameId);
    const evalAfter = evaluatePlacement(gameId, slotKey);

    if (evalAfter.penalty > 0) {
      const slot = parseSlotKey(slotKey);
      const gameMeta = gameMetaById.get(gameId);
      remainingIssues.push({
        gameId,
        gameName: gameMeta?.name || 'Unnamed game',
        green: slot.green,
        rink: slot.rink,
        reasons: evalAfter.details
          .filter((d) => d.reason)
          .map((d) =>
            d.playedPreviousRoundHere
              ? `${d.teamName} is still on the same rink as last round (${slot.green}${slot.rink}).`
              : `${d.teamName} would still be on ${slot.green}${slot.rink} for the ${ordinal(d.projectedCount)} time.`
          )
      });
    }
  });

  return {
    round,
    suggestions,
    remainingIssues
  };
};
